import React, { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

function Wordmark({ onClick }) {
  return (
    <a href="#top" onClick={onClick} data-testid="brand-wordmark" className="inline-flex items-baseline gap-2 select-none">
      <span className="font-display text-[20px] font-semibold tracking-tight text-ink">
        Sajilo<span className="text-blue">Health</span>
      </span>
      <span className="font-deva text-[12px] text-textsoft hidden sm:inline">सजिलो ल्याब्स</span>
    </a>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="inline-flex items-center rounded-full border border-line bg-white/70 backdrop-blur p-0.5" role="group" aria-label="Language">
      {[
        { id: "en", label: "EN" },
        { id: "ne", label: "नेपाली" },
      ].map((o) => (
        <button
          key={o.id}
          type="button"
          data-testid={`lang-${o.id}`}
          onClick={() => setLang(o.id)}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-all ${
            lang === o.id ? "bg-ink text-white" : "text-textsoft hover:text-ink"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { id: "unified", label: t.nav.platform },
    { id: "difference", label: t.nav.why },
    { id: "investors", label: t.nav.investors },
  ];
  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-paper/85 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="shell">
        <div className="flex items-center justify-between h-[68px]">
          <Wordmark onClick={close} />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-testid={`nav-${item.id}`}
                className="nav-link px-3.5 py-2 text-[13.5px] font-medium text-textsoft hover:text-ink transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <LangToggle lang={lang} setLang={setLang} />
            <a href="#demo" data-testid="header-cta-demo" className="btn btn-primary hidden sm:inline-flex">
              {t.nav.demo}
            </a>
            <button
              type="button"
              aria-label="Menu"
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line bg-white text-ink"
            >
              {open ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-line bg-paper/98 backdrop-blur-xl">
          <div className="shell py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <a key={item.id} href={`#${item.id}`} onClick={close} className="px-2 py-3 text-[15px] font-medium text-ink border-b border-line last:border-b-0">
                {item.label}
              </a>
            ))}
            <a href="#demo" onClick={close} className="btn btn-primary w-full mt-4">
              {t.nav.demo}
              <ArrowRight size={14} strokeWidth={2.2} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
